import type { EditTarget } from '@web-studio/shared-schemas';
import { EditableContent } from '../../../edit/EditableContent';
import { EditableMedia } from '../../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../../lib/responsiveImageSizes';
import { pickBlockField } from '../../../lib/localized';
import { parseMenuColumns, type MenuColumn, type MenuItem } from '../../../lib/parseMenuColumns';
import type { BlockRenderProps } from '../../../types';
import { MenuItemDietaryTags, MenuItemName, MenuItemOrderCode, MenuItemPriceSlot } from '../MenuItemTags';
import { MenuLegalLegend } from '../MenuLegalLegend';
import {
  isMenuColumnActive,
  isMenuItemActive,
  menuItemHasContent,
  resolveColumnImageUrl,
} from '../menuEditHelpers';

function GalleryItem({
  item,
  columnIndex,
  itemIndex,
  editable,
  activeEditTarget,
  locale,
}: {
  item: MenuItem;
  columnIndex: number;
  itemIndex: number;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
  locale: string;
}) {
  if (!menuItemHasContent(item)) {
    return null;
  }
  const itemActive = isMenuItemActive(
    activeEditTarget?.kind === 'menu-item' ? activeEditTarget : null,
    columnIndex,
    itemIndex,
  );
  return (
    <article
      className={['ws-menu-gallery__item', itemActive ? 'ws-edit-child--active' : '']
        .filter(Boolean)
        .join(' ')}
      {...(editable
        ? {
            'data-edit-child': 'menu-item' as const,
            'data-column-index': columnIndex,
            'data-item-index': itemIndex,
          }
        : {})}
    >
      <div className="ws-menu-gallery__item-row">
        <MenuItemOrderCode code={item.code} className="ws-menu-gallery__item-code" />
        <div className="ws-menu-gallery__item-copy">
          <MenuItemName
            item={item}
            locale={locale}
            className="ws-menu__item-name ws-menu-gallery__item-name"
            showOrderCode={false}
          />
          {item.description ? (
            <p className="ws-menu-gallery__item-desc">{item.description}</p>
          ) : null}
          <MenuItemDietaryTags item={item} locale={locale} />
        </div>
        <MenuItemPriceSlot price={item.price} className="ws-menu-gallery__item-price" />
      </div>
    </article>
  );
}

function GalleryBand({
  column,
  columnIndex,
  imageUrl,
  imageFieldKey,
  editable,
  activeEditTarget,
  locale,
  flip,
}: {
  column: MenuColumn;
  columnIndex: number;
  imageUrl?: string;
  imageFieldKey: string;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
  locale: string;
  flip: boolean;
}) {
  const columnTitle = column.title.trim();
  const columnActive = isMenuColumnActive(activeEditTarget, columnIndex);
  const imageSrc = imageUrl ? buildSiteImageProxyUrl(imageUrl, 900, 78) : undefined;
  const imageSrcSet = imageUrl
    ? buildSiteImageSrcSet(imageUrl, [400, 640, 900, 1200], 78)
    : undefined;

  return (
    <div
      className={['ws-menu-gallery__band', flip ? 'ws-menu-gallery__band--flip' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div className="ws-menu-gallery__visual">
        {imageUrl ? (
          <EditableMedia
            fieldKey={imageFieldKey}
            editable={editable}
            activeEditTarget={activeEditTarget}
            className="ws-menu-gallery__visual-frame"
            src={imageSrc}
            srcSet={imageSrcSet}
            sizes={SITE_IMAGE_SIZES.menuCategory}
            alt={columnTitle || 'Category'}
            width={900}
            height={720}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="ws-menu-gallery__visual-placeholder" aria-hidden />
        )}
        {columnTitle ? (
          <h3
            className={[
              'ws-menu-gallery__category',
              columnActive ? 'ws-edit-child--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            {...(editable
              ? {
                  'data-edit-child': 'menu-column' as const,
                  'data-column-index': columnIndex,
                }
              : {})}
          >
            {column.title}
          </h3>
        ) : null}
      </div>
      <div className="ws-menu-gallery__list">
        {column.items.map((item, itemIndex) => (
          <GalleryItem
            key={`${item.name}-${itemIndex}`}
            item={item}
            columnIndex={columnIndex}
            itemIndex={itemIndex}
            editable={editable}
            activeEditTarget={activeEditTarget}
            locale={locale}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Category gallery — each section is a photo band + typographic dish list.
 */
export function MenuCategoryGallery({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const sectionTitle = pickBlockField(block.content, 'sectionTitle', locale);
  const sectionSubtitle = pickBlockField(block.content, 'sectionSubtitle', locale);
  const menuDataRaw = pickBlockField(block.content, 'menuData', locale);
  const columns = parseMenuColumns(menuDataRaw);
  const target = isActive ? activeEditTarget : null;

  return (
    <section
      className={['ws-menu-gallery', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="menu"
    >
      <div className="ws-menu-gallery__inner">
        <header className="ws-menu-gallery__intro">
          <EditableContent
            as="h2"
            fieldKey="sectionTitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-gallery__title"
          >
            {sectionTitle}
          </EditableContent>
          <EditableContent
            fieldKey="sectionSubtitle"
            editable={editable}
            activeEditTarget={target}
            className="ws-menu-gallery__subtitle"
          >
            {sectionSubtitle}
          </EditableContent>
        </header>
        <div className="ws-menu-gallery__bands">
          {columns.map((column, columnIndex) => (
            <GalleryBand
              key={`${column.title}-${columnIndex}`}
              column={column}
              columnIndex={columnIndex}
              imageUrl={resolveColumnImageUrl(block, column, columnIndex)}
              imageFieldKey={`categoryImage${columnIndex}`}
              editable={editable}
              activeEditTarget={target}
              locale={locale}
              flip={columnIndex % 2 === 1}
            />
          ))}
        </div>
        <MenuLegalLegend columns={columns} locale={locale} />
      </div>
    </section>
  );
}
